'use client';

import React, { useEffect, useRef, useId } from 'react';
import { RichText } from '../RichText/RichText';
import CloseIcon from '../Icons/CloseIcon';
import CouponIcon from '../Icons/CouponIcon';
import PhoneIcon from '../Icons/PhoneIcon';
import { OfferDetailsProps } from '../OfferCard/types';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { formatOfferExpirationDate } from '../OfferCard/offerExpirationDate';
import { useLayoutContext } from '../Layout/LayoutProvider';
import CallRailReadyGate from '../CallRail/CallRailReadyGate';
import { buttonBase, buttonVariants } from '../Button/buttonStyles';

const OfferDetails: React.FC<OfferDetailsProps> = ({ data, isOpen, onClose }) => {
  const { callRailPhone } = useLayoutContext();
  const title = data?.title ?? '';
  const teaser = data?.teaser ?? '';
  const details = data?.details ?? null;
  const panelRef = useRef<HTMLDivElement>(null);
  const expirationDate = formatOfferExpirationDate(data?.expirationDate);
  const resolvedTelHref = callRailPhone ? `tel:${callRailPhone.replace(/\D/g, '')}` : undefined;
  const uniqueId = useId();
  const titleId = `offer-details-title-${uniqueId}`;

  useEffect(() => {
    if (!isOpen) return;

    // prevent the page shift caused by scrollbar
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    const panel = panelRef.current;
    const focusableSelectors =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

    const focusableElements = panel
      ? Array.from(panel.querySelectorAll<HTMLElement>(focusableSelectors))
      : [];

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    firstElement?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key === 'Tab' && focusableElements.length > 0) {
        if (event.shiftKey) {
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement?.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <div
      aria-hidden={!isOpen}
      inert={!isOpen}
      className={`fixed inset-0 z-100 transition-opacity duration-300 text-left! ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      <button
        type='button'
        aria-label='Close details panel'
        onClick={onClose}
        tabIndex={!isOpen ? -1 : undefined}
        className='absolute inset-0 bg-black/40 transition-opacity duration-300'
      />

      <div
        ref={panelRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        className={`absolute flex flex-col md:rounded-l-2xl right-0 top-0 h-full w-full max-w-md bg-white px-4 py-5 md:px-9 md:py-10 overflow-y-auto shadow-xl transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <button
          type='button'
          onClick={onClose}
          aria-label='Close details panel'
          tabIndex={!isOpen ? -1 : undefined}
          className='ml-auto'
        >
          <CloseIcon className='w-12 h-12 border-2 border-secondary-500 rounded-lg text-primary-500 hover:opacity/90 cursor-pointer' />
        </button>

        <div className='flex flex-col items-start gap-6'>
          <h2
            id={titleId}
            className='heading-2 text-primary-500 text-left!'
          >
            <CouponIcon
              aria-hidden='true'
              className='text-brand-icon w-15 h-15 mb-3'
            />
            {title || 'Offer Details'}
          </h2>

          <p className='copy-medium text-primary-500 text-left!'>{teaser}</p>

          <span className='text-xs text-primary-500 text-left!'>
            Offer expires {expirationDate}
          </span>

          {details ? (
            <div className='offer-disclaimer-wrapper border-t border-border-global pt-6'>
              <RichText
                data={{
                  content: details,
                  className: 'offer-disclaimer mt-3 text-left!',
                  options: {
                    renderNode: {
                      [BLOCKS.PARAGRAPH]: (_node, children) => (
                        <p className='text-sm text-primary-500'>{children}</p>
                      ),
                      [INLINES.HYPERLINK]: (node, children) => (
                        <a
                          href={node.data.uri}
                          className='underline hover:no-underline!'
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          {children}
                        </a>
                      ),
                    },
                  },
                }}
              />
            </div>
          ) : null}
        </div>

        <div className='mt-auto grid grid-cols-2 gap-4 w-full'>
          <a
            href='#schedule'
            tabIndex={!isOpen ? -1 : undefined}
            className={`${buttonBase} ${buttonVariants.primary} py-4 px-4 cursor-pointer text-center`}
          >
            Book Now
          </a>

          {resolvedTelHref ? (
            <CallRailReadyGate>
              <a
                href={resolvedTelHref}
                tabIndex={!isOpen ? -1 : undefined}
                aria-label={`Call Now ${callRailPhone}`}
                className={`${buttonBase} ${buttonVariants.heroSecondary} group py-4 px-4 whitespace-nowrap flex-1`}
              >
                <PhoneIcon className='h-4 w-4 fill-current text-brand-icon transition-colors duration-200' />

                <span className='md:hidden text-primary-500!'>Call Now</span>

                <span className='hidden md:inline transition-colors duration-200 text-primary-500! group-hover:opacity-90'>
                  {callRailPhone}
                </span>
              </a>
            </CallRailReadyGate>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default OfferDetails;
